/**
 * AdminSignatureAudit — Iteration 87.
 *
 * Manager-Übersicht aller signierten Angebote mit ihrem Dokumenten-Status
 * (signiertes PDF + Liefer-/Leistungsvertrag). Eine-Klick-Recovery, falls
 * ein Dokument fehlt (z.B. PROD-Altbestand vor Iter87 QR-Sign-Fix).
 *
 * Rollen: admin, sales_manager, sales_director, telesales_director,
 * super_admin (Server-seitig gefiltert).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, freshFileUrl } from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import {
  ShieldCheck, RefreshCw, FileText, FileCheck2, AlertTriangle,
  Loader2, Download, Search, ExternalLink, Wrench,
  History, ArrowUp, ArrowDown, ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtTs = (iso, display) => {
  if (display) return display;
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const HEALTH_STYLE = {
  ok:      { label: "Vollständig", color: "text-[#00E575]", bg: "bg-[#00E575]/10 border-[#00E575]/40" },
  partial: { label: "Unvollständig", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/40" },
  missing: { label: "Dokumente fehlen", color: "text-red-300", bg: "bg-red-500/10 border-red-500/40" },
};

export default function AdminSignatureAudit() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, ok: 0, partial: 0, missing: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/signature-audit");
      setItems(r.data.items || []);
      setStats(r.data.stats || { total: 0, ok: 0, partial: 0, missing: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signatur-Audit konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) =>
      (i.offer_number || "").toLowerCase().includes(s) ||
      (i.customer_name || "").toLowerCase().includes(s) ||
      (i.signer_name || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const rebuildSignedPdf = async (o) => {
    setBusyId({ id: o.offer_id, action: "rebuild" });
    try {
      const r = await api.post(`/offers/${o.offer_id}/rebuild-signed-pdf`);
      toast.success("Signiertes PDF neu erzeugt");
      if (r.data?.pdf_storage_path) window.open(freshFileUrl(r.data.pdf_storage_path), "_blank", "noopener");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neu-Erzeugung fehlgeschlagen");
    } finally { setBusyId(null); }
  };

  const buildLL = async (o) => {
    setBusyId({ id: o.offer_id, action: "ll" });
    try {
      const r = await api.post(`/offers/${o.offer_id}/liefer-leistung-vertrag`);
      toast.success("Liefer- und Leistungsvertrag erzeugt");
      if (r.data?.pdf_storage_path) window.open(freshFileUrl(r.data.pdf_storage_path), "_blank", "noopener");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vertrags-Erstellung fehlgeschlagen");
    } finally { setBusyId(null); }
  };

  return (
    <>
      <AppHeader />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-[#00E575]/15 border border-[#00E575]/40">
              <ShieldCheck size={22} className="text-[#00E575]" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-zinc-100">Signatur-Audit</h1>
              <p className="text-[13px] text-zinc-300 font-medium">
                Alle signierten Angebote + Status ihrer Vertragsdokumente
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="sig-audit-refresh"
            className="h-10 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Aktualisieren
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatCard label="Signiert (gesamt)" value={stats.total} tone="zinc" testid="sig-audit-total" />
          <StatCard label="Vollständig" value={stats.ok} tone="green" testid="sig-audit-ok" />
          <StatCard label="Unvollständig" value={stats.partial} tone="amber" testid="sig-audit-partial" />
          <StatCard label="Dokumente fehlen" value={stats.missing} tone="red" testid="sig-audit-missing" />
        </div>

        {/* Search */}
        <div className="mb-3 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="sig-audit-search"
            placeholder="Suche nach Angebotsnummer, Kunde oder Unterzeichner…"
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#18181B] border border-zinc-800 text-zinc-100 text-sm font-medium focus:outline-none focus:border-[#00E575]/50"
          />
        </div>

        {/* Table */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-zinc-400"><Loader2 className="animate-spin mx-auto mb-2" /> Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-zinc-400 text-sm font-medium">
              Keine signierten Angebote gefunden.
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {filtered.map((o) => {
                const hs = HEALTH_STYLE[o.health] || HEALTH_STYLE.missing;
                return (
                  <div key={o.offer_id} className="p-4" data-testid={`sig-audit-row-${o.offer_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm text-[#00E575] font-bold">{o.offer_number}</span>
                          {o.channel === "public-qr" && (
                            <span className="px-1.5 py-0.5 rounded bg-[#00E575]/15 text-[10px] uppercase tracking-wider font-bold text-[#00E575]">QR</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-extrabold uppercase tracking-wider ${hs.color} ${hs.bg} border`}>
                            {hs.label}
                          </span>
                        </div>
                        <div className="mt-1 text-[15px] font-bold text-zinc-100 truncate">{o.customer_name}</div>
                        <div className="mt-0.5 text-[12.5px] text-zinc-300 font-medium">
                          Signiert am <b className="text-zinc-100">{fmtTs(o.signed_at, o.signed_at_display)}</b>
                          {o.signer_name ? <> von <b className="text-zinc-100">{o.signer_name}</b></> : null}
                          <> · Auftrag <b className="text-zinc-100">{fmtEur(o.total_gross)}</b></>
                        </div>
                        {/* Doc chips */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <DocChip ok={o.has_signed_pdf} label="Signiertes Angebot" icon={FileText} />
                          <DocChip ok={o.has_liefer_leistung} label="Liefer-/Leistungsvertrag" icon={FileCheck2} />
                          {o.bza_reminder_sent && (
                            <span className="px-2 py-0.5 rounded-md text-[10.5px] font-bold text-zinc-300 bg-zinc-800 border border-zinc-700">BzA-Reminder gesendet</span>
                          )}
                          {o.bza_status && (
                            <span className="px-2 py-0.5 rounded-md text-[10.5px] font-bold text-sky-300 bg-sky-500/10 border border-sky-500/30">
                              BzA: {o.bza_status}
                            </span>
                          )}
                        </div>
                        {/* Iter92 — Diff zur Vorversion (nur wenn vorhanden).
                            Zeigt kompakt, was der Vertrieb vor der Signatur
                            gegenüber der letzten Fassung geändert hat. */}
                        {o.changes_vs_previous && (
                          <AuditDiffChip diff={o.changes_vs_previous} />
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/projects/${o.project_id}?tab=offers`)}
                          data-testid={`sig-audit-open-${o.offer_id}`}
                          className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                        >
                          <ExternalLink size={14} /> Projekt öffnen
                        </button>
                        {!o.has_signed_pdf && (
                          <button
                            onClick={() => rebuildSignedPdf(o)}
                            disabled={busyId?.id === o.offer_id}
                            data-testid={`sig-audit-rebuild-${o.offer_id}`}
                            className="h-9 px-3 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            {busyId?.id === o.offer_id && busyId?.action === "rebuild"
                              ? <Loader2 className="animate-spin" size={14} />
                              : <Wrench size={14} />}
                            Signiertes PDF
                          </button>
                        )}
                        {!o.has_liefer_leistung && (
                          <button
                            onClick={() => buildLL(o)}
                            disabled={busyId?.id === o.offer_id}
                            data-testid={`sig-audit-build-ll-${o.offer_id}`}
                            className="h-9 px-3 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 text-[#00E575] border border-[#00E575]/40 text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            {busyId?.id === o.offer_id && busyId?.action === "ll"
                              ? <Loader2 className="animate-spin" size={14} />
                              : <FileCheck2 size={14} />}
                            Vertrag erzeugen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneClass = {
    zinc: "text-zinc-100 border-zinc-800",
    green: "text-[#00E575] border-[#00E575]/30",
    amber: "text-amber-300 border-amber-500/30",
    red: "text-red-300 border-red-500/30",
  }[tone] || "text-zinc-100 border-zinc-800";
  return (
    <div className={`rounded-2xl p-4 bg-[#18181B] border ${toneClass}`} data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1">{label}</div>
      <div className="font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

function DocChip({ ok, label, icon: Icon }) {
  return (
    <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold inline-flex items-center gap-1 border ${
      ok ? "text-[#00E575] bg-[#00E575]/10 border-[#00E575]/30"
         : "text-red-300 bg-red-500/10 border-red-500/30"
    }`}>
      <Icon size={11} />
      {ok ? label : `${label} FEHLT`}
    </span>
  );
}

/**
 * AuditDiffChip — Iteration 92.
 *
 * Kompakter, aufklappbarer Chip auf jeder Audit-Zeile: zeigt Δ Preis + Anzahl
 * geänderter Positionen; auf Klick öffnet sich die vollständige Diff-Liste.
 */
function AuditDiffChip({ diff }) {
  const [open, setOpen] = useState(false);
  if (!diff) return null;
  const {
    vs_offer_number, total_gross_delta = 0, discount_delta = 0,
    items_added = [], items_removed = [], items_changed = [], item_change_count = 0,
  } = diff;
  const up = total_gross_delta > 0.005;
  const down = total_gross_delta < -0.005;

  return (
    <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden" data-testid="audit-diff-chip">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 hover:bg-zinc-800/50"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <History size={11} className="text-zinc-400 flex-shrink-0" />
          <span className="text-[11px] text-zinc-300 font-medium truncate">
            Änderungen ggü. <b className="text-zinc-100 font-mono">{vs_offer_number}</b>
            {item_change_count > 0 && <span className="ml-1 text-zinc-400">({item_change_count})</span>}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(up || down) && (
            <span className={`text-[11px] font-bold ${up ? "text-red-300" : "text-[#00E575]"}`}>
              {up ? <ArrowUp size={10} className="inline -mt-0.5" /> : <ArrowDown size={10} className="inline -mt-0.5" />}
              {" "}{fmtEur(Math.abs(total_gross_delta))}
            </span>
          )}
          <ChevronDown size={12} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-2.5 py-2 border-t border-zinc-800 space-y-1.5 text-[10.5px]">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 font-medium">Δ Gesamt</span>
            <span className={`font-mono font-bold ${up ? "text-red-300" : down ? "text-[#00E575]" : "text-zinc-300"}`}>
              {total_gross_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(total_gross_delta))}
            </span>
          </div>
          {Math.abs(discount_delta) > 0.005 && (
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 font-medium">Δ Rabatt</span>
              <span className={`font-mono font-bold ${discount_delta > 0 ? "text-amber-300" : "text-zinc-300"}`}>
                {discount_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(discount_delta))}
              </span>
            </div>
          )}
          {items_added.map((it, i) => (
            <div key={`a-${i}`} className="flex items-center justify-between gap-2">
              <span className="text-[#00E575] flex-shrink-0">+</span>
              <span className="text-zinc-100 truncate flex-1">{it.name || "—"}</span>
              <span className="text-zinc-400 font-mono flex-shrink-0">{fmtEur(it.line_net)}</span>
            </div>
          ))}
          {items_removed.map((it, i) => (
            <div key={`r-${i}`} className="flex items-center justify-between gap-2 line-through text-zinc-500">
              <span className="text-red-300 no-underline flex-shrink-0">−</span>
              <span className="truncate flex-1">{it.name || "—"}</span>
              <span className="font-mono flex-shrink-0">{fmtEur(it.line_net)}</span>
            </div>
          ))}
          {items_changed.map((it, i) => (
            <div key={`c-${i}`} className="flex items-center justify-between gap-2">
              <span className="text-amber-300 flex-shrink-0">~</span>
              <span className="text-zinc-100 truncate flex-1">{it.name || "—"}</span>
              <span className={`font-mono font-bold flex-shrink-0 ${it.line_delta > 0 ? "text-red-300" : "text-[#00E575]"}`}>
                {it.line_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(it.line_delta))}
              </span>
            </div>
          ))}
          {item_change_count === 0 && Math.abs(total_gross_delta) < 0.005 && (
            <div className="text-zinc-500 italic text-[10.5px]">Nur Metadaten-Änderungen.</div>
          )}
        </div>
      )}
    </div>
  );
}

